// ============================================================
// Platform Control · Webhooks — inbound endpoints and outbound
// delivery subscribers. Inbound section shows code-defined
// endpoints (Slack events, interactions). Outbound reads from
// usePlatformWebhooks() which queries sq_webhooks.
// ============================================================

import { useNavigate } from 'react-router-dom'
import { usePlatformWebhooks } from '../../../hooks/usePlatformWebhooks'
import ViewShell, { Card, Row, Section, ActionButton, ActionRow } from './_ViewShell'
import EmptyState from '../../../components/EmptyState'
import { SHELL_TOKENS } from '../../../shell/styles'

const INBOUND_ENDPOINTS = [
  {
    id: 'slack-events',
    endpoint: '/api/slack/events',
    label: 'Slack Events',
    verification: 'HMAC signature (SLACK_SIGNING_SECRET)',
    secretPresent: 'env-gated',
    status: 'real',
  },
  {
    id: 'slack-interactions',
    endpoint: '/api/slack/interactions',
    label: 'Slack Interactions',
    verification: 'HMAC signature (SLACK_SIGNING_SECRET)',
    secretPresent: 'env-gated',
    status: 'real',
  },
]

const OUTBOUND_HOOKS = [
  {
    id: 'slack-alerts',
    label: 'Slack Alert Delivery',
    target: 'SLACK_WEBHOOK_URL',
    powers: 'OLIVIUM critical/alert events via fireAlert()',
    status: 'real (env-gated)',
  },
  {
    id: 'slack-ops',
    label: 'Slack Ops Notify',
    target: 'SLACK_WEBHOOK_URL',
    powers: 'Workflow step notifications via opsNotify()',
    status: 'real (env-gated)',
  },
]

function copyToClipboard(text: string) {
  navigator.clipboard.writeText(text).catch(() => {})
}

export default function WebhooksView() {
  const { webhooks, loading, awaitingBackend } = usePlatformWebhooks()
  const navigate = useNavigate()

  return (
    <ViewShell
      kicker="PLATFORM CONTROL · WEBHOOKS"
      title="Webhooks"
      description="Inbound endpoints, outbound deliveries, and secret/verification state."
    >
      <Section label="INBOUND ENDPOINTS">
        <Card>
          {INBOUND_ENDPOINTS.map((ep) => (
            <div key={ep.id} style={{ padding: '10px 0', borderBottom: `1px solid ${SHELL_TOKENS.border}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: SHELL_TOKENS.ink }}>
                    {ep.label}
                  </div>
                  <div style={{ fontSize: 10, color: SHELL_TOKENS.inkMuted, fontFamily: 'var(--font-mono)', marginTop: 2 }}>
                    {ep.endpoint}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{
                    fontSize: 10,
                    color: SHELL_TOKENS.green,
                    fontFamily: 'var(--font-mono)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                  }}>
                    {ep.status}
                  </span>
                  <button
                    type="button"
                    onClick={() => copyToClipboard(ep.endpoint)}
                    style={btnStyle}
                  >
                    Copy
                  </button>
                </div>
              </div>
              <div style={{ fontSize: 10, color: SHELL_TOKENS.inkMuted, fontFamily: 'var(--font-mono)', marginTop: 4 }}>
                Verification: {ep.verification}
              </div>
            </div>
          ))}
        </Card>
      </Section>

      <Section label="OUTBOUND DELIVERY">
        <Card>
          {OUTBOUND_HOOKS.map((h) => (
            <Row
              key={h.id}
              label={h.label}
              value={h.status}
              hint={`${h.powers} → ${h.target}`}
            />
          ))}
          <ActionRow>
            <ActionButton onClick={() => navigate('/square/control/infrastructure')}>
              Check Provider Health
            </ActionButton>
            <ActionButton onClick={() => window.open('https://api.slack.com/apps', '_blank')}>
              Open Slack App Settings
            </ActionButton>
            <ActionButton onClick={() => navigate('/square/olivium/feed')}>
              View Delivery Events
            </ActionButton>
          </ActionRow>
        </Card>
      </Section>

      <Section label="WEBHOOK SUBSCRIBERS">
        {loading ? (
          <EmptyState title="Loading" body="Checking webhook configuration." />
        ) : awaitingBackend ? (
          <EmptyState
            title="Setup required"
            body="The webhooks backend has not been activated yet. Contact your platform administrator to complete setup."
          />
        ) : webhooks.length === 0 ? (
          <EmptyState
            title="No webhook subscribers"
            body="No webhooks are configured yet. Webhooks will appear here as event subscriptions are created."
          />
        ) : (
          <Card>
            {webhooks.map((w, i) => (
              <div
                key={w.id}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '12px 0',
                  borderBottom:
                    i === webhooks.length - 1
                      ? 'none'
                      : `1px solid ${SHELL_TOKENS.border}`,
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: SHELL_TOKENS.ink }}>
                    {w.label}
                  </div>
                  <div
                    style={{
                      fontSize: 10,
                      color: SHELL_TOKENS.inkMuted,
                      fontFamily: 'var(--font-mono)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      maxWidth: 480,
                    }}
                  >
                    {w.url}
                  </div>
                </div>
                <div
                  style={{
                    fontSize: 10,
                    color:
                      w.status === 'active'
                        ? SHELL_TOKENS.green
                        : w.status === 'failing'
                          ? SHELL_TOKENS.red
                          : SHELL_TOKENS.inkFaint,
                    fontFamily: 'var(--font-mono)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                  }}
                >
                  {w.status}
                </div>
              </div>
            ))}
          </Card>
        )}
      </Section>
    </ViewShell>
  )
}

const btnStyle: React.CSSProperties = {
  padding: '5px 10px',
  borderRadius: 6,
  border: `1px solid ${SHELL_TOKENS.border}`,
  background: 'transparent',
  color: SHELL_TOKENS.ink,
  fontSize: 10,
  cursor: 'pointer',
  fontFamily: 'var(--font-mono)',
}
